// Schemas and types for the sheets tool, shared by server and UI.
import { z } from 'zod';

/** Upper bounds that keep a workbook responsive in the browser and bounded on the server. */
export const SHEET_LIMITS = {
  sheets: 64,
  rows: 1_048_576,
  cols: 16_384,
  /** Non-empty cells across the whole workbook. */
  cells: 1_000_000,
  merges: 10_000,
  /** Cells covered by merged ranges across the whole workbook; the xlsx writer materializes each one. */
  mergedCells: 1_000_000,
  formulaLength: 8_192,
  textLength: 32_767,
} as const;

/** Layout defaults in CSS pixels at 100% zoom; font sizes are in points like Excel. */
export const SHEET_DEFAULTS = {
  colWidth: 88,
  rowHeight: 24,
  fontSize: 11,
  /** Grid size shown for a new or small sheet. */
  rows: 1000,
  cols: 26,
} as const;

const color = z.string().regex(/^#[0-9a-f]{6}$/i);

export const cellStyle = z.object({
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  underline: z.boolean().optional(),
  /** Points. */
  fontSize: z.number().min(1).max(409).optional(),
  color: color.optional(),
  fill: color.optional(),
  hAlign: z.enum(['left', 'center', 'right']).optional(),
  vAlign: z.enum(['top', 'middle', 'bottom']).optional(),
  wrap: z.boolean().optional(),
  /** Excel number format code, e.g. `#,##0.00`, `0%`, `dd.mm.yyyy`. */
  numFmt: z.string().max(255).optional(),
});
export type CellStyle = z.infer<typeof cellStyle>;

/** Canonical (English) error codes as stored in xlsx files. */
export const CELL_ERRORS = ['#DIV/0!', '#NAME?', '#VALUE!', '#REF!', '#NUM!', '#N/A', '#NULL!', '#CIRCULAR!'] as const;
export type CellErrorCode = (typeof CELL_ERRORS)[number];

/** How Turkish Excel shows each error; #CIRCULAR! is this app's marker for circular references. */
export const CELL_ERROR_LABELS: Record<CellErrorCode, string> = {
  '#DIV/0!': '#BÖL/0!',
  '#NAME?': '#AD?',
  '#VALUE!': '#DEĞER!',
  '#REF!': '#BAŞV!',
  '#NUM!': '#SAYI!',
  '#N/A': '#YOK',
  '#NULL!': '#BOŞ!',
  '#CIRCULAR!': '#DÖNGÜ!',
};

export const cellValue = z.union([
  z.number().finite(),
  z.string().max(SHEET_LIMITS.textLength),
  z.boolean(),
  z.object({ error: z.enum(CELL_ERRORS) }),
  z.null(),
]);
export type CellValue = z.infer<typeof cellValue>;

export const sheetCell = z.object({
  /** For formula cells, the cached result of the last calculation. */
  value: cellValue.optional(),
  /** Canonical formula without the leading "=": English function names, "," separators, "." decimals. */
  formula: z.string().min(1).max(SHEET_LIMITS.formulaLength).optional(),
  style: cellStyle.optional(),
});
export type SheetCell = z.infer<typeof sheetCell>;

// Addresses and indexes are bounded to Excel's grid (SHEET_LIMITS): writers allocate up to the furthest one.
const cellAddress = z
  .string()
  .regex(/^[A-Z]{1,3}[1-9]\d{0,6}$/)
  .refine((address) => parseCellName(address) !== null, 'cell outside the sheet');
const rangeAddress = z
  .string()
  .regex(/^[A-Z]{1,3}[1-9]\d{0,6}:[A-Z]{1,3}[1-9]\d{0,6}$/)
  .refine((range) => parseRangeName(range) !== null, 'range outside the sheet');
const indexKey = (limit: number) =>
  z
    .string()
    .regex(/^\d{1,7}$/)
    .refine((key) => Number(key) < limit, 'index outside the sheet');

export const sheetSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1)
    .max(31)
    .regex(/^[^\\/?*[\]:]+$/)
    .refine((name) => !name.startsWith("'") && !name.endsWith("'")),
  /** Keyed by A1 address. */
  cells: z.record(cellAddress, sheetCell),
  /** Zero-based column index → width in CSS pixels. */
  colWidths: z.record(indexKey(SHEET_LIMITS.cols), z.number().min(0).max(4000)),
  /** Zero-based row index → height in CSS pixels. */
  rowHeights: z.record(indexKey(SHEET_LIMITS.rows), z.number().min(0).max(2000)),
  merges: z.array(rangeAddress).max(SHEET_LIMITS.merges),
  frozen: z.object({
    rows: z.number().int().min(0).max(1000),
    cols: z.number().int().min(0).max(1000),
  }),
  showGridLines: z.boolean(),
});
export type SheetData = z.infer<typeof sheetSchema>;

export const workbookSchema = z
  .object({
    sheets: z.array(sheetSchema).min(1).max(SHEET_LIMITS.sheets),
    activeSheet: z.number().int().min(0),
  })
  .superRefine((book, ctx) => {
    const names = new Set<string>();
    let cells = 0;
    let mergedCells = 0;
    for (const sheet of book.sheets) {
      const key = sheet.name.toLocaleLowerCase('tr-TR');
      if (names.has(key)) ctx.addIssue({ code: 'custom', message: `duplicate sheet name "${sheet.name}"` });
      names.add(key);
      cells += Object.keys(sheet.cells).length;
      for (const range of sheet.merges) mergedCells += rangeArea(parseRangeName(range));
    }
    if (cells > SHEET_LIMITS.cells) ctx.addIssue({ code: 'custom', message: `more than ${SHEET_LIMITS.cells} cells` });
    if (mergedCells > SHEET_LIMITS.mergedCells) {
      ctx.addIssue({ code: 'custom', message: `merged ranges cover more than ${SHEET_LIMITS.mergedCells} cells` });
    }
    if (book.activeSheet >= book.sheets.length) ctx.addIssue({ code: 'custom', message: 'activeSheet out of range' });
  });
export type Workbook = z.infer<typeof workbookSchema>;

export const sheetImportInput = z.object({ fileId: z.uuid() });

export const SHEET_EXPORT_FORMATS = ['xlsx', 'csv', 'pdf'] as const;
export type SheetExportFormat = (typeof SHEET_EXPORT_FORMATS)[number];

export const sheetExportInput = z.object({
  workbook: workbookSchema,
  format: z.enum(SHEET_EXPORT_FORMATS),
  target: z.union([
    z.object({ fileId: z.uuid() }),
    z.object({ name: z.string().trim().min(1).max(255), parentId: z.uuid().nullable().optional() }),
  ]),
});
export type SheetExportInput = z.infer<typeof sheetExportInput>;

/** Zero-based column index → letters (0 → A, 26 → AA). */
export function columnName(index: number): string {
  let name = '';
  let n = index + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

/** Column letters → zero-based index, or -1 when invalid. */
export function columnIndex(letters: string): number {
  let index = 0;
  for (const char of letters.toUpperCase()) {
    const code = char.charCodeAt(0);
    if (code < 65 || code > 90) return -1;
    index = index * 26 + (code - 64);
  }
  return index - 1;
}

export function cellName(row: number, col: number): string {
  return `${columnName(col)}${row + 1}`;
}

/** "B12" → { row: 11, col: 1 }; null for anything else. */
export function parseCellName(name: string): { row: number; col: number } | null {
  const match = /^([A-Za-z]{1,3})(\d{1,7})$/.exec(name.trim());
  if (!match) return null;
  const row = Number(match[2]) - 1;
  const col = columnIndex(match[1]!);
  if (row < 0 || row >= SHEET_LIMITS.rows || col < 0 || col >= SHEET_LIMITS.cols) return null;
  return { row, col };
}

export interface CellRange {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

/** "B2:A10" → zero-based, normalized corners; null when either corner is outside the sheet. */
export function parseRangeName(range: string): CellRange | null {
  const [first, second, ...rest] = range.split(':');
  if (first === undefined || second === undefined || rest.length) return null;
  const a = parseCellName(first);
  const b = parseCellName(second);
  if (!a || !b) return null;
  return { top: Math.min(a.row, b.row), left: Math.min(a.col, b.col), bottom: Math.max(a.row, b.row), right: Math.max(a.col, b.col) };
}

/** Number of cells in a range; 0 for null. */
export function rangeArea(range: CellRange | null): number {
  return range ? (range.bottom - range.top + 1) * (range.right - range.left + 1) : 0;
}

export function emptySheet(name: string): SheetData {
  return { name, cells: {}, colWidths: {}, rowHeights: {}, merges: [], frozen: { rows: 0, cols: 0 }, showGridLines: true };
}
